// CompanyLogo — public/-Stack-Variante (Drift-Map D2, 2026-05-21).
//
// Portiert aus app/-Stack (app/src/components/editor/Viewer/CompanyLogo.tsx),
// mit zwei Anpassungen:
//
//   1. Branding-Source:
//        app/-Stack: brandingLogo: { url; position: 'left'|'center'|'none'|'hidden' }
//                    (V1 form-layer Shape, prop-down von ScrollyViewer)
//        public/:    BrandingTokens V2 via useBrandingTokens() Hook
//                    (tokens.logo.light.{url, position: 'L'|'M'|'R'})
//
//   2. Z-Index: Z.logo (=80) statt hardcoded 2000.
//
// Resolution-Logic (1:1 von app/):
//   0. `companyLogoPosition` 'none' | 'hidden' am Scrolly → KEIN Logo (Scrolly
//      gewinnt ueber Org, seit 07.09.2026 — vorher nur im URL-Zweig beachtet)
//   1. Per-Template `slideOptions.companyLogoUrl` + `companyLogoPosition` (Legacy-Override)
//      — V1-Position-Werte: 'left' | 'center'
//   2. Fallback: BrandingTokens-Light-Variant (URL); Position vom Scrolly, falls gesetzt
//      — V2-Position-Werte: 'L' | 'M' | 'R'
//
// Position-Harmonisierung (V1 + V2):
//   'L' | 'left'   → justify flex-start
//   'M' | 'center' → justify center
//   'R'            → justify flex-end (V2-only, V1 hat kein "right")
//   'none' | 'hidden' (V1) → nicht rendern
//
// Dark-Mode-Variant (`tokens.logo.dark`) ist NICHT genutzt — Plan-2-Backlog
// für Color-Mode-aware Logo-Switch. Heute default light-Variante (= Logo
// designed für helle Hintergründe, was der typische Scrolly-Textbox-Case ist).
//
// **Visual-Verify-Note (Browser-Check beim D2-Deploy):**
// `position: fixed; top: 0` überlagert PhoneFrame-Notch potenziell. Im
// app/-Stack saß das Logo so — im Immersive-Mode visuell sauber, im
// Phone-Mode möglicherweise hässlich über der Status-Leiste. Beim D2-
// Browser-Test verifizieren ob das Mode-aware werden muss (Phone:
// inside-Stage-Top-Bar; Immersive: fixed-top).

import type { PostJson } from "@scrolly/post-types";
import { useBrandingTokens } from "./branding-context";
import { Z } from "../../lib/z-index";

interface Props {
  post?: PostJson | null;
}

interface SlideLogoOptions {
  companyLogoUrl?: string;
  companyLogoPosition?: string;
}

export function CompanyLogo({ post }: Props) {
  const tokens = useBrandingTokens();

  // Per-Template-Legacy-Override aus slideOptions (V1)
  const slideOptions = (post?.data as { data?: { presentationOptions?: { slideOptions?: SlideLogoOptions } } } | undefined)
    ?.data?.presentationOptions?.slideOptions ?? {};
  const { companyLogoUrl, companyLogoPosition } = slideOptions;

  // „Was am Scrolly gesetzt ist, gewinnt." (BRANDING-USECASE-UND-STAND, Teil A)
  // Logo „Aus" im Scrolly (Pro-Editor seit 04.09.2026, Template-Editor-Mapping
  // 'none' → 'hidden') heisst: KEIN Logo — auch nicht das der Organisation.
  // Vorher galt 'none' nur fuer den Legacy-Per-Template-URL-Zweig; ohne
  // eigene companyLogoUrl (der Pro-Editor setzt nur die Position) fiel der
  // Viewer auf das Org-Logo zurueck, obwohl Editor und Vorschau es
  // ausblendeten (Jans iPhone-Befund 07.09.2026). Ebenso gewinnt eine im
  // Scrolly gesetzte Position (links/mitte) ueber die Position der Org.
  if (companyLogoPosition === "none" || companyLogoPosition === "hidden") {
    return null;
  }

  let logoUrl: string | null | undefined;
  let logoPosition: string | undefined;

  if (companyLogoUrl) {
    logoUrl = companyLogoUrl;
    logoPosition = companyLogoPosition;
  } else if (tokens.logo.light.url) {
    logoUrl = tokens.logo.light.url;
    logoPosition = companyLogoPosition || tokens.logo.light.position;
  }

  if (!logoUrl) return null;

  // V1 + V2 Position-Harmonisierung
  const justify =
    logoPosition === "center" || logoPosition === "M"
      ? "center"
      : logoPosition === "R"
        ? "flex-end"
        : "flex-start"; // default: L / left

  return (
    <div
      style={{
        // Block 1 (2026-05-22): position:absolute statt fixed — Logo
        // verankert sich am PhoneFrame.innerStyle (position:relative)
        // statt am Viewport. Im Immersive-Modus analog: ImmersiveCanvas
        // baseStyle ist position:relative.
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        zIndex: Z.logo,
        display: "flex",
        width: "100%",
        overflow: "hidden",
        scrollbarGutter: "stable",
        justifyContent: justify,
        pointerEvents: "none",
      }}
    >
      <div style={{ margin: "12px 16px", height: 40, pointerEvents: "auto" }}>
        <img
          src={logoUrl}
          alt="Logo"
          style={{
            height: "100%",
            width: "auto",
            objectFit: "contain",
            display: "block",
          }}
        />
      </div>
    </div>
  );
}

export default CompanyLogo;
